import React, { useState } from 'react';
import { RefreshCw, PauseCircle, XCircle, Mail, Loader2 } from 'lucide-react';
import type { PaymentInfo } from '../../../data/mockSubscriptionData';
interface PaymentStatusBannerProps {
  paymentInfo: PaymentInfo;
  customerEmail: string;
  onRetryNow: () => void;
  onSendPaymentLink: () => void;
}
// Helper to format next retry time
const formatNextRetry = (nextRetry?: string): string => {
  if (!nextRetry) return '';
  const retryDate = new Date(nextRetry);
  const now = new Date();
  const diffMs = retryDate.getTime() - now.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  if (diffHours < 24) {
    return `Tomorrow at ${retryDate.toLocaleTimeString('en-AU', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    })}`;
  }
  return retryDate.toLocaleDateString('en-AU', {
    weekday: 'short',
    day: 'numeric',
    month: 'short'
  }) + ' at ' + retryDate.toLocaleTimeString('en-AU', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};
// Helper to get plain language failure reason
const getFailureReasonText = (reason?: string): string => {
  if (!reason) return '';
  const reasonMap: Record<string, string> = {
    insufficient_funds: 'Card declined - Insufficient funds',
    'Insufficient funds': 'Card declined - Insufficient funds',
    card_expired: 'Card expired - Customer needs to update payment method',
    'Card expired': 'Card expired - Customer needs to update payment method',
    card_declined: 'Card declined by issuing bank',
    'Card declined': 'Card declined by issuing bank',
    'Bank declined': 'Card declined by issuing bank',
    invalid_card: 'Payment method is invalid or has been removed',
    'Payment method revoked': 'Payment method is invalid or has been removed',
    processing_error: 'Temporary processing error - Will retry automatically',
    authentication_required: 'Card requires authentication - Customer action needed',
    'Max retries exhausted': 'All retry attempts exhausted - Awaiting customer action',
    'Awaiting customer action': 'All retry attempts exhausted - Awaiting customer action'
  };
  return reasonMap[reason] || reason;
};
export function PaymentStatusBanner({
  paymentInfo,
  customerEmail,
  onRetryNow,
  onSendPaymentLink
}: PaymentStatusBannerProps) {
  const [isRetrying, setIsRetrying] = useState(false);
  const [isSendingLink, setIsSendingLink] = useState(false);
  // Don't show banner for healthy payments
  if (!paymentInfo || paymentInfo.status === 'healthy') {
    return null;
  }
  const handleRetryNow = async () => {
    setIsRetrying(true);
    try {
      await onRetryNow();
    } finally {
      setIsRetrying(false);
    }
  };
  const handleSendLink = async () => {
    setIsSendingLink(true);
    try {
      await onSendPaymentLink();
    } finally {
      setIsSendingLink(false);
    }
  };
  // Determine banner styling based on status
  const getBannerStyles = () => {
    switch (paymentInfo.status) {
      case 'in-retry':
        return {
          bg: 'bg-[#FEF3C7]',
          border: 'border-l-[#F59E0B]',
          icon: RefreshCw,
          iconColor: 'text-[#F59E0B]',
          title: 'Payment Retry in Progress'
        };
      case 'on-hold':
        return {
          bg: 'bg-[#FEF9C3]',
          border: 'border-l-[#EAB308]',
          icon: PauseCircle,
          iconColor: 'text-[#EAB308]',
          title: 'Subscription On Hold'
        };
      case 'failed':
        return {
          bg: 'bg-[#FEE2E2]',
          border: 'border-l-[#EF4444]',
          icon: XCircle,
          iconColor: 'text-[#EF4444]',
          title: 'Payment Failed'
        };
      default:
        return null;
    }
  };
  const styles = getBannerStyles();
  if (!styles) return null;
  const Icon = styles.icon;
  const isFinalRetry = paymentInfo.retryCount === paymentInfo.maxRetries;
  // Build subtitle text
  let subtitle = '';
  if (paymentInfo.status === 'in-retry') {
    subtitle = `Retry ${paymentInfo.retryCount} of ${paymentInfo.maxRetries}`;
    if (paymentInfo.nextRetry) {
      subtitle += ` • Next retry: ${formatNextRetry(paymentInfo.nextRetry)}`;
    }
  } else if (paymentInfo.status === 'on-hold') {
    subtitle = 'All retry attempts exhausted • Waiting for customer to update payment';
  } else if (paymentInfo.status === 'failed') {
    subtitle = 'Payment method is no longer valid';
  }
  const failureReasonText = getFailureReasonText(paymentInfo.failureReason);
  return <div className={`${styles.bg} border-l-4 ${styles.border} rounded-lg p-4 mb-6`} data-id="element-3056">
      <div className="flex items-start gap-4" data-id="element-3057">
        {/* Icon */}
        <div className="flex-shrink-0 mt-0.5" data-id="element-3058">
          <Icon className={`w-5 h-5 ${styles.iconColor}`} data-id="element-3059" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0" data-id="element-3060">
          <h3 className="text-[16px] font-semibold text-[#0F172A] mb-1" data-id="element-3061">
            {styles.title}
          </h3>
          <p className="text-[14px] text-[#475569] mb-1" data-id="element-3062">{subtitle}</p>
          {failureReasonText && <p className="text-[14px] text-[#64748B]" data-id="element-3063">{failureReasonText}</p>}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 flex-shrink-0" data-id="element-3064">
          {paymentInfo.status === 'in-retry' && !isFinalRetry && <button onClick={handleRetryNow} disabled={isRetrying} className="inline-flex items-center gap-2 h-10 px-4 bg-white border border-[#CBD5E1] rounded-md text-[#0F172A] text-[14px] font-medium hover:bg-[#F1F5F9] hover:border-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:ring-opacity-20 transition-all disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-3065">
              {isRetrying ? <>
                  <Loader2 className="w-4 h-4 animate-spin" data-id="element-3066" />
                  <span data-id="element-3067">Retrying...</span>
                </> : <>
                  <RefreshCw className="w-4 h-4" data-id="element-3068" />
                  <span data-id="element-3069">Retry Now</span>
                </>}
            </button>}

          <button onClick={handleSendLink} disabled={isSendingLink} className="inline-flex items-center gap-2 h-10 px-4 bg-[#0EA5E9] border border-[#0EA5E9] rounded-md text-white text-[14px] font-medium hover:bg-[#0284C7] hover:border-[#0284C7] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:ring-opacity-20 transition-all disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-3070">
            {isSendingLink ? <>
                <Loader2 className="w-4 h-4 animate-spin" data-id="element-3071" />
                <span data-id="element-3072">Sending...</span>
              </> : <>
                <Mail className="w-4 h-4" data-id="element-3073" />
                <span data-id="element-3074">Send Payment Link</span>
              </>}
          </button>
        </div>
      </div>
    </div>;
}